import React, { useEffect, useState } from 'react';
import { Table, Button, Spin, Input, Row, Col, message, Modal } from 'antd';
import jsPDF from 'jspdf';
import apiRequest from '../hooks/ApiRequest';
import Layouts from '../components/Layouts';

const ClaimRejectedReport = () => {
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [statusFilter] = useState('Rejected');
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        fetchClaimReport(statusFilter);
    }, [statusFilter]);

    const fetchClaimReport = async (status) => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const insurerId = user?.UserId;
            if (!insurerId) {
                message.error("Insurer ID not found in user data");
                setLoading(false);
                return;
            }

            const apiUrl = `${process.env.REACT_APP_BASE_URL_CLAIM}/Claim/GetClaimReport/ClaimReport/${insurerId}?status=${status}`;
            const res = await apiRequest(apiUrl, 'GET');
            const data = res?.claims || res?.data?.claims || [];
            setReportData(data);
            setFilteredData(data);
        } catch (error) {
            console.error('Error fetching claim report:', error);
            message.error("Failed to fetch claim report.");
            setReportData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!searchText) {
            setFilteredData(reportData);
        } else {
            const search = searchText.toLowerCase();
            const filtered = reportData.filter((item) =>
                item.claimNumber?.toLowerCase().includes(search) ||
                item.brokerName?.toLowerCase().includes(search) ||
                item.customerName?.toLowerCase().includes(search)
            );
            setFilteredData(filtered);
        }
    }, [searchText, reportData]);

    const clearFilters = () => {
        setSearchText('');
        setFilteredData(reportData);
    };

    // 🔹 Export table data to CSV
    const exportToCSV = () => {
        if (!filteredData || filteredData.length === 0) {
            message.warning("No data available to export.");
            return;
        }

        const csvHeader = [
            "Claim Number",
            "Broker Name",
            "Customer Name",
            "Claim Amount",
            "Approved Amount",
            "Claim Status",
        ];

        const csvRows = filteredData.map((item) => [
            item.claimNumber || "-",
            item.brokerName || "-",
            item.customerName || "-",
            item.claimAmount || "0",
            item.approvedAmount || "0",
            item.claimStatus || "-",
        ]);

        const csvContent = [
            csvHeader.join(","),
            ...csvRows.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "ClaimRejectedReport.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 🔹 Fetch claim details by ID
    const handleViewDetails = async (claimId) => {
        try {
            const apiUrl = `${process.env.REACT_APP_BASE_URL_CLAIM}/Claim/GetClaimById/${claimId}`;
            const res = await apiRequest(apiUrl, 'GET');

            // 🔹 unwrap depending on apiRequest wrapper
            const claimData = res?.result || res?.data?.result || res;
            if (claimData?.claim_creation) {
                setSelectedClaim(claimData);
                setModalVisible(true);
            } else {
                message.error("Claim details not found.");
            }
        } catch (error) {
            console.error("Error fetching claim details:", error);
            message.error("Failed to fetch claim details.");
        }
    };

    // 🔹 Download claim as PDF
    const exportClaimToPDF = (claim) => {
        if (!claim) return;

        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text("Claim Report", 10, 10);

        const yStart = 20;
        let y = yStart;

        const addLine = (label, value) => {
            doc.text(`${label}: ${value || ''}`, 10, y);
            y += 8;
        };

        addLine("Claim Number", claim.claim_creation?.claim_number);
        addLine("Policy Number", claim.claim_creation?.policy_number);
        addLine("Customer Name", claim.claim_creation?.customer_name);
        addLine("Customer Email", claim.claim_creation?.customer_email);
        addLine("Claim Amount", claim.claim_creation?.claim_amount);
        addLine("Approved Amount", claim.claim_creation?.approved_amount);
        addLine("Status", claim.claim_creation?.claim_status);

        if (claim.claim_activity?.length > 0) {
            y += 5;
            doc.text("Activities:", 10, y);
            y += 8;
            claim.claim_activity.forEach((a, i) => {
                doc.text(
                    `${i + 1}. ${a.activity_status} - ${a.user_name} (${new Date(a.created_date).toLocaleString()})`,
                    10,
                    y
                );
                y += 8;
            });
        }

        doc.save(`Claim_${claim.claim_creation?.claim_number}.pdf`);
    };

    const columns = [
        { key: 'claimNumber', title: 'Claim Number', dataIndex: 'claimNumber' },
        { key: 'brokerName', title: 'Broker Name', dataIndex: 'brokerName' },
        { key: 'customerName', title: 'Customer Name', dataIndex: 'customerName' },
        { key: 'claimAmount', title: 'Claim Amount', dataIndex: 'claimAmount' },
        { key: 'approvedAmount', title: 'Approved Amount', dataIndex: 'approvedAmount' },
        { key: 'claimStatus', title: 'Claim Status', dataIndex: 'claimStatus' },
        {
            key: 'action',
            title: 'Action',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => handleViewDetails(record.claimId || record.id)}
                >
                    View
                </Button>
            )
        }
    ];

    return (
        <Layouts title="Claim Rejected Report" classname="dashboard">
            <Spin spinning={loading} size="large" className="loader-icon-fixed" />

            <div className="dash-bg-white">
                <Row gutter={[16, 16]}>
                    <Col xs={12} sm={24} md={24} lg={12}>
                        <div className="section-top-heading">
                            <h3 style={{ color: '#0C1362', fontWeight: 600, fontSize: '20px' }}>
                                Claim Report ({statusFilter})
                            </h3>
                        </div>
                    </Col>
                    <Col xs={12} sm={24} md={24} lg={12} style={{ textAlign: 'right' }}>
                        <Button type="primary" onClick={exportToCSV}>
                            Export
                        </Button>
                    </Col>
                </Row>
            </div>

            <div className="dash-bg-white m-b-20">
                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} md={18} lg={20}>
                        <Input
                            placeholder="Search by Claim No /Customer /Broker"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                            allowClear
                        />
                        &nbsp;&nbsp;
                        <Button onClick={clearFilters}>Clear</Button>
                    </Col>
                </Row>
            </div>

            <div className="table-grid-bx">
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    rowKey={record => record.claimId || record.id}
                    pagination={{ pageSize: 10 }}
                />
            </div>

            {/* 🔹 Modal to show details */}
            <Modal
                visible={modalVisible}
                title="Claim Details"
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button
                        key="pdf"
                        type="primary"
                        onClick={() => exportClaimToPDF(selectedClaim)}
                    >
                        Download PDF
                    </Button>,
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {selectedClaim ? (
                    <div>
                        <p><b>Claim Number:</b> {selectedClaim.claim_creation?.claim_number}</p>
                        <p><b>Customer:</b> {selectedClaim.claim_creation?.customer_name}</p>
                        <p><b>Email:</b> {selectedClaim.claim_creation?.customer_email}</p>
                        <p><b>Claim Amount:</b> {selectedClaim.claim_creation?.claim_amount}</p>
                        <p><b>Status:</b> {selectedClaim.claim_creation?.claim_status}</p>
                        <h4>Activities:</h4>
                        <ul>
                            {selectedClaim.claim_activity?.map(act => (
                                <li key={act.id}>
                                    {act.activity_status} - {act.user_name} (
                                    {new Date(act.created_date).toLocaleString()})
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <p>No details found.</p>
                )}
            </Modal>
        </Layouts>
    );
};

export default ClaimRejectedReport;
